import type { JSX } from "react";
import { useState } from "react";
import type React from "react";
import { useLiveQuery } from "dexie-react-hooks";
import { db } from "../../sync/db.js";
import { Button } from "@/ui/button.js";
import { Input } from "@/ui/input.js";
import { Label } from "@/ui/label.js";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/ui/sheet.js";

interface CreateListSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (name: string, recipeIds: string[]) => Promise<void>;
}

export function CreateListSheet({
  open,
  onOpenChange,
  onSubmit,
}: CreateListSheetProps): JSX.Element {
  const [name, setName] = useState("");
  const [selectedRecipeIds, setSelectedRecipeIds] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);

  // Exclude pending photo-import drafts from the recipe picker.
  const recipes = useLiveQuery(() => db.recipes.filter((r) => r.status !== "pending").toArray(), []);
  const sortedRecipes = (recipes ?? [])
    .slice()
    .sort((a, b) => a.title.localeCompare(b.title));

  function toggleRecipe(id: string): void {
    setSelectedRecipeIds((prev) =>
      prev.includes(id) ? prev.filter((r) => r !== id) : [...prev, id],
    );
  }

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) return;
    setSubmitting(true);
    try {
      await onSubmit(trimmed, selectedRecipeIds);
      setName("");
      setSelectedRecipeIds([]);
    } finally {
      setSubmitting(false);
    }
  }

  function handleOpenChange(next: boolean): void {
    if (!next) {
      setName("");
      setSelectedRecipeIds([]);
    }
    onOpenChange(next);
  }

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>New shopping list</SheetTitle>
          <SheetDescription>
            Name your list and optionally select recipes to generate items from.
          </SheetDescription>
        </SheetHeader>

        <form
          onSubmit={(e) => {
            void handleSubmit(e);
          }}
          className="flex flex-col gap-4 mt-4"
        >
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="list-name">List name</Label>
            <Input
              id="list-name"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
              }}
              placeholder="e.g. Weekly shop"
              autoFocus
              maxLength={120}
              aria-required="true"
            />
          </div>

          {sortedRecipes.length > 0 && (
            <div className="flex flex-col gap-1.5">
              <Label>Add from recipes (optional)</Label>
              <div className="flex flex-col gap-0.5 max-h-52 overflow-y-auto rounded-md border border-zinc-800 p-2">
                {sortedRecipes.map((recipe) => (
                  <label
                    key={recipe.id}
                    className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-zinc-800 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={selectedRecipeIds.includes(recipe.id)}
                      onChange={() => {
                        toggleRecipe(recipe.id);
                      }}
                      className="accent-emerald-500 h-4 w-4 shrink-0"
                    />
                    <span className="text-sm text-zinc-200 truncate">{recipe.title}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          <SheetFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                handleOpenChange(false);
              }}
              disabled={submitting}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!name.trim() || submitting}>
              {submitting ? "Creating…" : "Create list"}
            </Button>
          </SheetFooter>
        </form>
      </SheetContent>
    </Sheet>
  );
}
